import { useState, useEffect, useEffectEvent } from 'react';
import { useMutation, useQuery } from '@apollo/client/react';
import { ErrorLike } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import { DialogContentText } from '@mui/material';

import {
    IModuleType,
    DeleteModuleDocument,
    ModuleEditDocument,
    UpdateFileModuleDocument,
} from 'graphql/types';
import { useSnackbar } from 'common/hooks/useSnackbar';
import { useApolloError } from 'common/hooks/useApolloError';
import { PageTitle } from 'common/components/PageTitle';
import {
    FileModuleForm,
    IFileModuleFormProps,
} from 'training/components/forms/FileModuleForm';
import { IModuleUpdateDrawerProps } from 'training/types';
import { dayHoursToSeconds } from 'common/utils/formatDate';
import { Loader } from 'common/components/Loader';
import { getModuleOffsetFormValue } from 'training/utils/moduleOffset';
import { AlertDialog } from 'common/components/AlertDialog';
import { Button } from 'common/components/Button';
import { getImageId } from 'common/utils/image';

export const FileModuleUpdateDrawer = ({
    id,
    onClose,
}: IModuleUpdateDrawerProps) => {
    const [translate] = useTranslation();
    const [displaySnackbar] = useSnackbar();
    const { showApolloError } = useApolloError();

    const [showDeleteAlert, setShowDeleteAlert] = useState<boolean>(false);

    const { data, loading, error } = useQuery(ModuleEditDocument, {
        variables: { id, type: IModuleType.File },
    });

    const onError = useEffectEvent((error: ErrorLike) => {
        showApolloError(error);
    });

    useEffect(() => {
        if (error) {
            onError(error);
        }
    }, [error]);

    const [updateModule] = useMutation(UpdateFileModuleDocument);
    const [deleteModule, { loading: loadingDelete }] =
        useMutation(DeleteModuleDocument);

    const module = data?.module;

    if (loading || !module) return <Loader />;

    if (!module || module.__typename !== 'FileModule') return null;

    const handleSubmit: IFileModuleFormProps['onSubmit'] = async (values) => {
        const { file, image, offset, ...otherValues } = values;
        const { image: currentImage } = module;

        const moduleValues = {
            ...otherValues,
            fileId: file?.id,
            imageId: getImageId(currentImage, image),
            offset: {
                seconds: dayHoursToSeconds(offset?.days, offset?.hours),
                type: offset?.type,
            },
        };

        try {
            await updateModule({
                variables: { id, module: moduleValues },
            });
        } catch (error) {
            showApolloError(error);

            return;
        }

        displaySnackbar(translate('moduleActionSuccess.file.update'), {
            variant: 'success',
        });

        // Close drawer
        onClose?.();

        return;
    };

    const handleDelete = async () => {
        try {
            await deleteModule({
                variables: {
                    id,
                    type: IModuleType.File,
                },
                update: (cache) => {
                    cache.evict({ id: `FileModule:${id}` });
                    cache.gc();
                },
            });
        } catch (error) {
            showApolloError(error);

            return;
        }

        displaySnackbar(translate('moduleActionSuccess.file.delete'), {
            variant: 'success',
        });

        // Close drawer
        onClose?.();

        return;
    };

    const {
        title,
        description,
        file,
        image,
        openOffset,
        moduleGroup: { id: moduleGroupId },
    } = module;

    const initialValues = {
        title,
        description,
        image: image || undefined,
        file: file || undefined,
        offset: getModuleOffsetFormValue(openOffset),
    };

    return (
        <>
            <PageTitle>{translate('updateFileModule')}</PageTitle>

            <FileModuleForm
                edit
                initialValues={initialValues}
                moduleGroupId={moduleGroupId}
                onClose={onClose}
                onDelete={() => setShowDeleteAlert(true)}
                onSubmit={handleSubmit}
            />

            <AlertDialog
                actions={
                    <>
                        <Button
                            autoFocus
                            color="error"
                            loading={loadingDelete}
                            variant="contained"
                            onClick={handleDelete}
                        >
                            {translate('delete')}
                        </Button>
                        <Button
                            disabled={loadingDelete}
                            variant="contained"
                            onClick={() => {
                                setShowDeleteAlert(false);
                            }}
                        >
                            {translate('cancel')}
                        </Button>
                    </>
                }
                open={showDeleteAlert}
                title={translate('deleteFileModuleMessage.title')}
            >
                <DialogContentText color="text.primary" variant="body2">
                    {translate('deleteFileModuleMessage.text')}
                </DialogContentText>
            </AlertDialog>
        </>
    );
};
